import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";
import "./StudentAddressDetails.css";

const emptyAddress = {
  residenceAddress: "",
  residenceCity: "",
  residenceState: "",
  residenceCountry: "",
  residencePincode: "",
  residencePhone: "",
  permanentAddress: "",
  permanentCity: "",
  permanentState: "",
  permanentCountry: "",
  permanentPincode: "",
  permanentPhone: "",
};

const fieldValue = (value) => (value && value !== "-" ? value : "");

const StudentAddressDetails = () => {
  const navigate = useNavigate();
  const [addressData, setAddressData] = useState(emptyAddress);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStudentAddress = async () => {
      const studentId = sessionStorage.getItem("userId");

      if (!studentId) {
        setError("Student session details are missing. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentAddress/${studentId}/`
        );

        const contentType = response.headers.get("content-type") || "";
        const payload = contentType.includes("application/json")
          ? await response.json()
          : null;

        if (!response.ok || !payload || payload.message !== "Success") {
          throw new Error(payload?.message || "Unable to load student address.");
        }

        const address = payload.data || {};

        setAddressData({
          residenceAddress: address.present_address || "-",
          residenceCity: address.present_district || "-",
          residenceState: address.present_state || "-",
          residenceCountry: address.present_country || "-",
          residencePincode: address.present_pincode || "-",
          residencePhone: address.present_phone_number || "-",
          permanentAddress: address.permanent_address || "-",
          permanentCity: address.permanent_district || "-",
          permanentState: address.permanent_state || "-",
          permanentCountry: address.permanent_country || "-",
          permanentPincode: address.permanent_pincode || "-",
          permanentPhone: address.permanent_phone_number || "-",
        });
      } catch (fetchError) {
        console.error("Error fetching student address:", fetchError);
        setAddressData(emptyAddress);
        setError(fetchError.message || "Unable to load student address.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentAddress();
  }, []);

  const isPermanentSameAsResidence =
    JSON.stringify({
      address: fieldValue(addressData.residenceAddress),
      city: fieldValue(addressData.residenceCity),
      state: fieldValue(addressData.residenceState),
      country: fieldValue(addressData.residenceCountry),
      pincode: fieldValue(addressData.residencePincode),
      phone: fieldValue(addressData.residencePhone),
    }) ===
    JSON.stringify({
      address: fieldValue(addressData.permanentAddress),
      city: fieldValue(addressData.permanentCity),
      state: fieldValue(addressData.permanentState),
      country: fieldValue(addressData.permanentCountry),
      pincode: fieldValue(addressData.permanentPincode),
      phone: fieldValue(addressData.permanentPhone),
    });

  const renderInput = (label, value, className = "student-address-field col-12 col-md-6") => (
    <div className={className}>
      <label className="student-address-label">{label}</label>
      <input
        type="text"
        className="form-control student-address-input"
        value={value || "-"}
        readOnly
      />
    </div>
  );

  return (
    <div className="container-fluid student-address-page">
      <div className="row">
        <div className="col-12">
          <div className="student-address-shell">
            <div className="student-address-toolbar">
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-danger student-address-close"
                  onClick={() => navigate("/student/dashboards")}
                >
                  Close
                </button>
              </div>
            </div>

            {loading ? (
              <div className="student-address-feedback">Loading student address...</div>
            ) : error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : (
              <>
                <div className="row g-4 student-address-cards">
                  <div className="col-12 col-xl-6">
                    <div className="student-address-card">
                      <div className="student-address-card-title">Residence Address</div>
                      <div className="student-address-card-body">
                        {renderInput(
                          "Address",
                          addressData.residenceAddress,
                          "student-address-field col-12"
                        )}
                        {renderInput("Country*", addressData.residenceCountry)}
                        {renderInput("State*", addressData.residenceState)}
                        {renderInput("City/District*", addressData.residenceCity)}
                        {renderInput("Pincode*", addressData.residencePincode)}
                        {renderInput(
                          "Phone",
                          addressData.residencePhone,
                          "student-address-field col-12"
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-xl-6">
                    <div className="student-address-card">
                      <div className="student-address-card-title">Permanent Address</div>
                      <div className="student-address-card-body">
                        {renderInput(
                          "Address",
                          addressData.permanentAddress,
                          "student-address-field col-12"
                        )}
                        {renderInput("Country", addressData.permanentCountry)}
                        {renderInput("State", addressData.permanentState)}
                        {renderInput("City/District", addressData.permanentCity)}
                        {renderInput("Pincode", addressData.permanentPincode)}
                        {renderInput(
                          "Phone",
                          addressData.permanentPhone,
                          "student-address-field col-12"
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="student-address-checkbox-row">
                  <label className="student-address-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isPermanentSameAsResidence}
                      readOnly
                      disabled
                    />
                    <span>Permanent address is same as Residence</span>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAddressDetails;
